import { isAndroid, isCordova, isIOS } from '../util/mobile/deviceDetection';
import { logError } from '../util/log';

// Bridge to the native Firebase Analytics SDK inside the Cordova apps.
// Main plugin: cordova-plugin-firebase-analytics (logEvent, setUserId, ...).
// Shim plugin: cordova-plugin-vansite-firebase-shim (getAppInstanceId,
// getSessionId, setConsent — gaps in the main plugin's API).
// Both namespaces attach after deviceready; every call guards availability.

const SESSION_ID_REFRESH_MS = 60 * 1000;

const nativePlugin = () =>
  (typeof window !== 'undefined' && window.cordova?.plugins?.firebase?.analytics) || null;

const shimPlugin = () =>
  (typeof window !== 'undefined' && window.cordova?.plugins?.vansiteFirebaseShim) || null;

/**
 * True when running inside Cordova AND the native analytics plugin is ready.
 * Before deviceready this is false — callers fall back to no-op, and the
 * deviceready bootstrap re-applies consent to catch up.
 */
export const hasNativeAnalytics = () => !!(isCordova() && nativePlugin());

/** 'android' | 'ios' | null — the app stream this device reports to. */
export const nativeAppPlatform = () => {
  if (!isCordova()) return null;
  if (isAndroid()) return 'android';
  if (isIOS()) return 'ios';
  return null;
};

// The native Android bridge (cordova-plugin-firebase-analytics) cannot
// convert String arrays into the JSONObject it hands to the Android SDK
// (VANSITE-FRONTEND-4MA — e.g. `classes` on button-click events). Scalar
// arrays are flattened to a joined string; anything else non-scalar is
// serialized. Only the native path needs this — gtag/web accepts arrays.
const isScalar = (value) =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';

const sanitizeNativeParams = (params) =>
  Object.entries(params).reduce((sanitized, [key, value]) => {
    if (value == null) {
      // drop null/undefined
    } else if (isScalar(value)) {
      sanitized[key] = value;
    } else if (Array.isArray(value) && value.every(isScalar)) {
      sanitized[key] = value.join(',');
    } else {
      sanitized[key] = JSON.stringify(value);
    }
    return sanitized;
  }, {});

export const nativeLogEvent = async (name, params = {}) => {
  const plugin = nativePlugin();
  if (!plugin) return;
  try {
    await plugin.logEvent(name, sanitizeNativeParams(params));
    refreshNativeSessionId();
  } catch (e) {
    logError(e, 'native-firebase-log-event', { eventName: name });
  }
};

export const nativeSetUserId = async (userId) => {
  const plugin = nativePlugin();
  if (!plugin) return;
  try {
    await plugin.setUserId(userId ?? null);
  } catch (e) {
    logError(e, 'native-firebase-set-user-id');
  }
};

export const nativeSetUserProperties = async (properties = {}) => {
  const plugin = nativePlugin();
  if (!plugin) return;
  try {
    for (const [key, value] of Object.entries(properties)) {
      if (value != null) await plugin.setUserProperty(key, String(value));
    }
  } catch (e) {
    logError(e, 'native-firebase-set-user-properties');
  }
};

/**
 * Toggle native collection (GDPR opt-in switch) and forward Consent Mode v2
 * signals. `resetData` additionally wipes the on-device analytics data —
 * used when a previously granted consent is revoked (GDPR Art. 7(3)).
 * @param {{statistics: boolean, marketing: boolean}} effective - effective consent
 * @param {{resetData?: boolean}} [options]
 */
export const applyNativeConsent = async (effective, { resetData = false } = {}) => {
  const plugin = nativePlugin();
  if (!plugin) return;
  try {
    const shim = shimPlugin();
    if (shim) {
      const signal = (granted) => (granted ? 'granted' : 'denied');
      await shim.setConsent({
        analytics_storage: signal(effective.statistics),
        ad_storage: signal(effective.marketing),
        ad_user_data: signal(effective.marketing),
        ad_personalization: signal(effective.marketing),
      });
    }
    await plugin.setEnabled(!!effective.statistics);
    if (effective.statistics) {
      await refreshNativeTrackingIds();
    } else {
      clearCachedNativeIds();
      if (resetData) await plugin.resetAnalyticsData();
    }
  } catch (e) {
    logError(e, 'native-firebase-apply-consent');
  }
};

// --- Native tracking ids (for the server-side Measurement Protocol mirror) ---
// The shim APIs are async, but collectTrackingIds() is consumed synchronously
// in the event relay — so the ids are cached here and refreshed opportunistically
// (on consent grant and throttled after each logged event; the session rotates).

let cachedAppInstanceId = null;
let cachedSessionId = null;
let lastSessionRefresh = 0;

const clearCachedNativeIds = () => {
  cachedAppInstanceId = null;
  cachedSessionId = null;
  lastSessionRefresh = 0;
};

export const getCachedNativeIds = () => ({
  appInstanceId: cachedAppInstanceId,
  appSessionId: cachedSessionId,
  appPlatform: hasNativeAnalytics() ? nativeAppPlatform() : null,
});

export const refreshNativeTrackingIds = async () => {
  const shim = shimPlugin();
  if (!shim) return;
  try {
    cachedAppInstanceId = (await shim.getAppInstanceId()) || null;
    cachedSessionId = (await shim.getSessionId()) || null;
    lastSessionRefresh = Date.now();
  } catch (e) {
    logError(e, 'native-firebase-refresh-ids');
  }
};

const refreshNativeSessionId = () => {
  const shim = shimPlugin();
  if (!shim || Date.now() - lastSessionRefresh < SESSION_ID_REFRESH_MS) return;
  lastSessionRefresh = Date.now();
  Promise.resolve()
    .then(async () => {
      if (!cachedAppInstanceId) cachedAppInstanceId = (await shim.getAppInstanceId()) || null;
      cachedSessionId = (await shim.getSessionId()) || cachedSessionId;
    })
    .catch(() => {});
};
